// Lib
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import HttpStatus from 'http-status-codes';
import { connect } from 'react-redux';
import { compose, withProps, withState, withHandlers } from '../../../../../node_module_clones/recompose';
import { get, isEmpty } from 'lodash/fp';
import classNames from 'classnames';

// Utils
import { selectAllText, selectEndOfText } from '../../../../utils/domUtil';
import { cleanEmailList } from '../../../../../common/utils/stringUtil';
import multiEmailValidator from '../../../../../common/validation/validators/type/multiEmailValidator';
import { getResponseStatusCode } from '../../../../utils/errorUtil';

// Selectors
import { boardEditorAddSectionSelector } from './userSelectionInputSelector';

// Components
import fetchGoogleContactsOnMountDecorator from '../../../../user/google/components/fetchGoogleContactsOnMountDecorator';
import ListFocusManager from '../../../lists/ListFocusManager';
import ListKeyboardManager, { LIST_DIRECTIONS } from '../../../lists/ListKeyboardManager';
import UserSelectionInputSuggestions from './UserSelectionInputSuggestions';
import PrimaryErrorMessage from '../../messages/PrimaryErrorMessage';
import StyledButton from '../../../buttons/StyledButton';
import StyledPopupInputContainer from '../StyledPopupInputContainer';

// Constants
import { KEY_CODES } from '../../../../utils/keyboard/keyConstants';

// Styles
import './UserSelectionInput.scss';

const enhance = compose(
    fetchGoogleContactsOnMountDecorator,
    withState('error', 'setError', null),
    withState('searchTerm', 'setSearchTerm', ''),
    withState('focused', 'setFocused', false),
    withState('hovered', 'setHovered', false),
    withState('loading', 'setLoading', false),
    withState('forceClose', 'setForceClose', false),
    connect(boardEditorAddSectionSelector),
    withProps((ownProps) => ({
        initialFocusIndex: -1,
        listSize: ownProps.suggestedUsers ? ownProps.suggestedUsers.length : 0,
    })),
    ListFocusManager(), // eslint-disable-line new-cap
    withHandlers({
        // Ensure the suggestions popup doesn't get stuck in a hovered state
        setForceClose:
            ({ setForceClose, setHovered }) =>
            (forced) => {
                setHovered(false);
                return setForceClose(forced);
            },
        handleAddEditor:
            ({ handleSubmitSelectUser, setFocusIndex, setError, setLoading, setSearchTerm }) =>
            async ({ user }) => {
                setError(null);
                setFocusIndex(-1);
                setSearchTerm('');

                let result;

                setLoading(true);

                try {
                    result = await handleSubmitSelectUser({ user });
                } catch (error) {
                    if (getResponseStatusCode(error) === HttpStatus.TOO_MANY_REQUESTS) {
                        setError('Too many invites, wait 30 seconds');
                    }
                }

                setLoading(false);

                return result;
            },
        handleSubmitInputText:
            ({ handleSubmitSelectUserViaText, setSearchTerm, setError, setLoading }) =>
            async ({ inputText }) => {
                // Perform all client validation immediately here so we can clear the input immediately after
                // client validation is successful
                const validationError = multiEmailValidator(inputText);
                if (validationError) {
                    return validationError.value && validationError.value.length > 1
                        ? setError('Multiple emails are invalid')
                        : setError(validationError.message);
                }

                // Split text based on commas or spaces
                const emails = cleanEmailList(inputText);
                if (isEmpty(emails)) return;

                if (emails.length > 20) return setError('A maximum of 20 users can be added at once');

                setError(null);
                setSearchTerm('');
                setLoading(true);

                let result;

                try {
                    result = await handleSubmitSelectUserViaText({ inputText });
                } catch (err) {
                    if (getResponseStatusCode(err) === HttpStatus.TOO_MANY_REQUESTS) {
                        setError('Too many invites, wait 30 seconds');
                    }
                }

                setLoading(false);

                return result;
            },
    }),
    withHandlers({
        onClose:
            ({ forceClose, setForceClose }) =>
            (event) => {
                if (forceClose) return;

                event.preventDefault();
                event.stopPropagation();

                setForceClose(true);
            },
        onSubmit:
            ({ focusedIndex, suggestedUsers, handleAddEditor, handleSubmitInputText }) =>
            (event) => {
                if (focusedIndex >= 0) {
                    const focusedUser = get(focusedIndex, suggestedUsers);
                    return focusedUser && handleAddEditor({ user: focusedUser });
                }

                return handleSubmitInputText({ inputText: event.target.value });
            },
    }),
    ListKeyboardManager({ listDirection: LIST_DIRECTIONS.VERTICAL }), // eslint-disable-line new-cap
);

const UserSelectionInput = (props) => {
    const {
        error,
        formError,
        loading,
        setFocusIndex,
        forceClose,
        setForceClose,
        setSearchTerm,
        setError,
        searchTerm,
        keyboardInputRef,
        setFocused,
        handleAddEditor,
        handleSubmitInputText,
        label,
        submitLabel = 'Add',
        details,
        placeholder = 'Enter a name or email addresses',
    } = props;

    const containerRef = React.useRef(null);

    // Select all text on enter
    useEffect(() => {
        if (!keyboardInputRef || !keyboardInputRef.current) return;

        const onKeyDown = (event) => {
            if (event.keyCode === KEY_CODES.ENTER) {
                setForceClose(true);
                selectAllText(keyboardInputRef.current);
            }

            if (event.keyCode === KEY_CODES.DOWN_ARROW && forceClose) {
                setForceClose(false);
                setFocusIndex(-1);
                selectEndOfText(keyboardInputRef.current);
            }
        };

        keyboardInputRef.current.addEventListener('keydown', onKeyDown);

        return () => keyboardInputRef.current && keyboardInputRef.current.removeEventListener('keydown', onKeyDown);
    }, [forceClose]);

    useEffect(() => {
        if (containerRef?.current) {
            const width = containerRef.current?.offsetWidth;
            document.body.style.setProperty('--user-selection-input-width', `${width}px`);
        }
    }, []);

    const onSetSearchTerm = (event) => {
        setSearchTerm(event.target.value);
        setFocusIndex(-1);
        setError(null);
        if (forceClose && isEmpty(event.target.value)) setForceClose(false);
    };

    const onFocus = () => {
        setFocused(true);
        props.onFocus && props.onFocus();
    };

    const onBlur = () => {
        setForceClose(false);
        setFocusIndex(-1);
        setFocused(false);
    };

    const onInputClick = () => setForceClose(false);

    const onSuggestionClick = (args) => {
        setForceClose(true);
        return handleAddEditor(args);
    };

    const onAddButtonClick = () => handleSubmitInputText({ inputText: searchTerm });
    const enableAddButton = searchTerm.length > 0;

    return (
        <div className="UserSelectionInput" ref={containerRef}>
            {!!label && (
                <div className="label-container">
                    <label htmlFor="editor-email-input" className="email-input-label">
                        {label}
                    </label>
                </div>
            )}
            {details}
            <div className="email-input-container">
                <div id="email-input-suggestions-target" className="email-input-suggestions-target" />
                <StyledPopupInputContainer inputRef={keyboardInputRef}>
                    <input
                        className={classNames('FormInput email-input', { error: !!error })}
                        ref={keyboardInputRef}
                        id="editor-email-input"
                        placeholder={placeholder}
                        value={searchTerm}
                        onClick={onInputClick}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onChange={onSetSearchTerm}
                        autoComplete="off"
                    />
                </StyledPopupInputContainer>
                <StyledButton
                    className="primary thin"
                    onClick={onAddButtonClick}
                    disabled={!enableAddButton || loading}
                >
                    {submitLabel}
                </StyledButton>
            </div>
            <UserSelectionInputSuggestions {...props} handleAddUserFromSuggestion={onSuggestionClick} />

            <PrimaryErrorMessage show={!!(formError || error)} message={formError || error} icon={null} />
        </div>
    );
};

UserSelectionInput.propTypes = {
    handleSubmitSelectUser: PropTypes.func.isRequired,
    handleSubmitSelectUserViaText: PropTypes.func.isRequired,
    label: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.string]),
    submitLabel: PropTypes.string,
    onFocus: PropTypes.func,
    formError: PropTypes.string,
    error: PropTypes.string,
    keyboardInputRef: PropTypes.object,
    suggestedUsers: PropTypes.array,
    setSearchTerm: PropTypes.func,
    setError: PropTypes.func,
    searchTerm: PropTypes.string,
    setFocused: PropTypes.func,
    setForceClose: PropTypes.func,
    setFocusIndex: PropTypes.func,
    forceClose: PropTypes.bool,
    loading: PropTypes.bool,
    handleAddEditor: PropTypes.func,
    handleSubmitInputText: PropTypes.func,
    placeholder: PropTypes.string,
    details: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.string]),
};

export default enhance(UserSelectionInput);
